import { GroupedInstruments, ParsedSymbol } from '@/features/chart/lib/configs/options-config'
import type { OpenInstrumentTabParams } from '@/features/dashboard/hook/use-watchlist-tab'
import { resolveBaseCurrencyFromTicker } from '@/features/order/order-form/lib/instrument-resolution'
import { toExchangeString } from '@/lib/proto/util'
import { safeParseFloat } from '@/lib/utils'
import { Exchange, Instrument, InstrumentKind, OptionType } from '@/types/generated/common/types'

export const MONTHS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
]

/** Convert millisecond timestamp string to Deribit-style expiry label like "17DEC27" */
export function expirationTsMsToExpiry(tsMs: string): string {
  const d = new Date(Number(tsMs))
  const day = d.getUTCDate()
  const month = MONTHS[d.getUTCMonth()]
  const year = String(d.getUTCFullYear()).slice(2)
  return `${day}${month}${year}`
}

/** Parse an option symbol string into components. Handles Deribit and OPRA (Databento) formats. */
function parseOptionSymbolFromString(symbol: string): ParsedSymbol | null {
  // Deribit format: BTC-26DEC25-90000-C or BTC_USDC-26DEC25-90000-C
  // Some venues append a quote-currency suffix: ETH-25DEC26-1300-P-USDT
  const deribitMatch = symbol.match(
    /^([A-Z]+(?:_[A-Z]+)?)-(\d{1,2}[A-Z]{3}\d{2})-([0-9d.]+)-([CP])(?:-([A-Z]+))?$/i
  )
  if (deribitMatch) {
    const [, symbolTicker, expiry, strike, type, quoteSuffix] = deribitMatch
    const baseCurrency = resolveBaseCurrencyFromTicker([], symbolTicker)
    const ticker = quoteSuffix
      ? composeOptionTicker(baseCurrency, quoteSuffix.toUpperCase())
      : symbolTicker.toUpperCase()
    return {
      ticker,
      baseCurrency,
      expiry,
      strike: safeParseFloat(strike.replace(/d/i, '.')),
      type: type.toUpperCase() as 'C' | 'P',
    }
  }

  // OPRA format (Databento): IBIT_271217P00048000 (ROOT_YYMMDD{C|P}STRIKE×1000)
  const opraMatch = symbol.match(/^([A-Z]+)_(\d{6})([CP])(\d+)$/)
  if (opraMatch) {
    const [, root, dateStr, type, strikeRaw] = opraMatch
    const year = 2000 + parseInt(dateStr.slice(0, 2))
    const month = parseInt(dateStr.slice(2, 4)) - 1
    const day = parseInt(dateStr.slice(4, 6))
    const expiry = `${day}${MONTHS[month]}${String(year).slice(2)}`
    const strike = parseInt(strikeRaw) / 1000
    return {
      ticker: root,
      baseCurrency: root,
      expiry,
      strike,
      type: type.toUpperCase() as 'C' | 'P',
    }
  }

  return null
}

/** Parse an Instrument object into option components, using explicit fields when available. */
function parseOptionSymbolFromInstrument(inst: Instrument): ParsedSymbol | null {
  const structuredBase = inst.baseCurrency?.trim().toUpperCase()
  const structuredQuote = inst.quoteCurrency?.trim().toUpperCase()
  const structuredTicker = structuredBase
    ? composeOptionTicker(structuredBase, structuredQuote || undefined)
    : null

  if (
    inst.strikePrice != null &&
    inst.expirationTsMs != null &&
    inst.optionType != null &&
    inst.optionType !== OptionType.OPTION_TYPE_UNSPECIFIED &&
    inst.optionType !== OptionType.UNRECOGNIZED
  ) {
    const strike = safeParseFloat(inst.strikePrice)
    if (isNaN(strike)) return null
    const expiry = expirationTsMsToExpiry(inst.expirationTsMs)
    const type = inst.optionType === OptionType.OPTION_TYPE_CALL ? 'C' : 'P'
    const symbolParsed = structuredBase ? null : parseOptionSymbolFromString(inst.symbol)
    const baseCurrency = structuredBase ?? symbolParsed?.baseCurrency
    const ticker = structuredTicker ?? symbolParsed?.ticker
    if (!baseCurrency || !ticker) return null
    return {
      ticker,
      baseCurrency,
      expiry,
      expirationTsMs: inst.expirationTsMs,
      strike,
      type,
    }
  }

  const symbolParsed = parseOptionSymbolFromString(inst.symbol)
  if (!symbolParsed) return null
  if (!structuredBase || !structuredTicker) return symbolParsed
  return {
    ...symbolParsed,
    ticker: structuredTicker,
    baseCurrency: structuredBase,
  }
}

// Per-Instrument parse cache, keyed by object identity. `Instrument` references stay
// stable across React Query cache reads for the same query, so this is shared across
// every widget/exchange tab that touches the same instrument list — even when a caller
// passes a derived array (e.g. `instruments.filter(...)`), since the individual element
// references inside it are unchanged. Both buildOptionSymbolMap and
// filterAndGroupInstruments parse every option instrument, so this also removes the
// duplicate parse work between the two.
const parsedSymbolByInstrument = new WeakMap<Instrument, ParsedSymbol | null>()

function parseOptionSymbolCached(inst: Instrument): ParsedSymbol | null {
  const cached = parsedSymbolByInstrument.get(inst)
  if (cached !== undefined) return cached
  const parsed = parseOptionSymbolFromInstrument(inst)
  parsedSymbolByInstrument.set(inst, parsed)
  return parsed
}

export function parseOptionSymbol(instrument: Instrument): ParsedSymbol | null
export function parseOptionSymbol(symbol: string): ParsedSymbol | null
export function parseOptionSymbol(instrumentOrSymbol: Instrument | string): ParsedSymbol | null {
  if (typeof instrumentOrSymbol === 'string') {
    return parseOptionSymbolFromString(instrumentOrSymbol)
  }
  return parseOptionSymbolFromInstrument(instrumentOrSymbol)
}

const optionMapsByInstrumentMap = new WeakMap<Map<string, Instrument>, Map<string, ParsedSymbol>>()

/** Reuse a venue's option lookup instead of scanning its catalog for each mounted widget. */
export function getOptionSymbolMapForInstruments(
  instruments: Map<string, Instrument>
): Map<string, ParsedSymbol> {
  const cached = optionMapsByInstrumentMap.get(instruments)
  if (cached) return cached
  const map = buildOptionSymbolMap(Array.from(instruments.values()))
  optionMapsByInstrumentMap.set(instruments, map)
  return map
}

/**
 * Build a symbol -> ParsedSymbol lookup from the instrument list, using the structured
 * proto fields (strikePrice/expirationTsMs/optionType/baseCurrency) rather than regex.
 */
export function buildOptionSymbolMap(instruments: Instrument[]): Map<string, ParsedSymbol> {
  const map = new Map<string, ParsedSymbol>()
  for (const inst of instruments) {
    if (inst.kind !== InstrumentKind.INSTRUMENT_KIND_OPTION) continue
    const parsed = parseOptionSymbolCached(inst)
    if (parsed) map.set(inst.symbol, parsed)
  }
  return map
}

/**
 * Resolve a ParsedSymbol for a raw option symbol string, preferring the instrument-derived
 * symbolMap (structured fields) and falling back to regex parsing of the symbol string for
 * symbols not present in the current instrument list.
 */
export function resolveOptionSymbol(
  symbol: string,
  symbolMap: Map<string, ParsedSymbol>
): ParsedSymbol | null {
  return symbolMap.get(symbol) ?? parseOptionSymbolFromString(symbol)
}

/**
 * Same as resolveOptionSymbol, but for the cross-exchange `useOptionSymbolMap()` hook's
 * per-exchange maps — the same symbol string can carry different structured data (strike/
 * expiry/type) on two exchanges, so this looks up the row's own exchange's map specifically
 * instead of one map shared across every exchange (which would let one exchange's entry
 * silently shadow another's for a colliding symbol).
 */
export function resolveOptionSymbolForExchange(
  exchange: Exchange,
  symbol: string,
  symbolMaps: Map<Exchange, Map<string, ParsedSymbol>>
): ParsedSymbol | null {
  return resolveOptionSymbol(symbol, symbolMaps.get(exchange) ?? new Map())
}

export function parseExpiryDate(expiry: string): Date {
  // "26DEC25" or "5JAN26" → Date
  // Use regex to properly parse day (1-2 digits), month (3 chars), year (2 digits)
  const match = expiry.match(/^(\d{1,2})([A-Z]{3})(\d{2})$/)
  if (!match) {
    return new Date(0) // Invalid format fallback
  }

  const [, dayStr, monthStr, yearStr] = match
  const day = parseInt(dayStr)
  const year = 2000 + parseInt(yearStr)

  const months: Record<string, number> = {
    JAN: 0,
    FEB: 1,
    MAR: 2,
    APR: 3,
    MAY: 4,
    JUN: 5,
    JUL: 6,
    AUG: 7,
    SEP: 8,
    OCT: 9,
    NOV: 10,
    DEC: 11,
  }

  return new Date(Date.UTC(year, months[monthStr], day))
}

export function getExpiryTimestamp(expiry: string): number {
  return parseExpiryDate(expiry).getTime()
}

export function isExpiryStillAvailable(expiry: string, expiryTsMs?: number | null): boolean {
  const now = Date.now()
  if (expiryTsMs != null && Number.isFinite(expiryTsMs)) {
    return expiryTsMs > now
  }

  // Exchange instrument lists may expose daily expiries that settle later than UTC
  // midnight. Keep the expiry date selectable through the whole UTC day when an
  // exact timestamp is unavailable.
  return getExpiryTimestamp(expiry) + 24 * 60 * 60 * 1000 > now
}

export function filterAndGroupInstruments(
  instruments: Instrument[]
): Map<string, GroupedInstruments> {
  const grouped = new Map<string, GroupedInstruments>()

  instruments.forEach((inst) => {
    const parsed = parseOptionSymbolCached(inst)
    if (!parsed) return

    if (!grouped.has(parsed.ticker)) {
      grouped.set(parsed.ticker, {
        ticker: parsed.ticker,
        expires: new Map(),
        expiryTsMs: new Map(),
      })
    }

    const group = grouped.get(parsed.ticker)!

    if (!group.expires.has(parsed.expiry)) {
      group.expires.set(parsed.expiry, [])
    }

    group.expires
      .get(parsed.expiry)!
      .push({ symbol: inst.symbol, strike: parsed.strike, type: parsed.type })

    if (inst.expirationTsMs != null && !group.expiryTsMs.has(parsed.expiry)) {
      group.expiryTsMs.set(parsed.expiry, Number(inst.expirationTsMs))
    }
  })

  return grouped
}

export function getNearesetExpiry(expires: string[]): string | null {
  if (expires.length === 0) return null

  const now = Date.now()
  const sorted = expires
    .map((exp) => ({ exp, time: getExpiryTimestamp(exp) }))
    // Buffer by 24 hours to ensure today's expiry is not filtered out just because it's past midnight
    .filter(({ time }) => time + 86400000 > now)
    .sort((a, b) => a.time - b.time)

  return sorted[0]?.exp ?? expires[0]
}

export function getStrikesForExpiry(group: GroupedInstruments, expiry: string): number[] {
  const entries = group.expires.get(expiry) ?? []

  const strikes = new Set<number>()
  entries.forEach((entry) => strikes.add(entry.strike))

  return [...strikes].sort((a, b) => a - b)
}

/**
 * Compose an option chain "ticker family" string from base/quote currency codes.
 * Contract: `base`/`quote` must already be uppercase — this function does not
 * normalize case. No quote, a `USD` quote (the implicit default), or a self-quoted
 * inverse contract (`BTC`/`BTC`) yields base only; any other quote is appended as
 * `BASE_QUOTE` (e.g. `BTC_USDC`).
 */
export function composeOptionTicker(base: string, quote: string | undefined): string {
  return quote && quote !== 'USD' && quote !== base ? `${base}_${quote}` : base
}

/**
 * Resolve the underlying quote represented by a canonical option-family ticker.
 * A base-only family (`BTC`) represents the implicit USD index; an explicit pair
 * (`BTC_USDT`, `BTC_USDC`) preserves its quote after the final underscore.
 *
 * This parses our canonical family key, not an exchange instrument symbol. Callers
 * with an Instrument should first build the key from its structured currency fields.
 */
export function resolveOptionTickerQuoteCurrency(ticker: string): string {
  const normalized = ticker.trim().toUpperCase()
  const separatorIndex = normalized.lastIndexOf('_')
  return separatorIndex >= 0 ? normalized.slice(separatorIndex + 1) : 'USD'
}

/**
 * Derive the option chain ticker family for an instrument — i.e. the exact key
 * `filterAndGroupInstruments` groups that instrument's option chain under.
 *
 * For an actual option instrument, this reuses `parseOptionSymbol` so expiry/strike/
 * type remain available. When an Instrument record is present, that parser derives
 * the family from the record's structured `baseCurrency`/`quoteCurrency`; symbol-name
 * parsing is reserved for raw-string or incomplete-instrument fallbacks.
 *
 * For a non-option instrument (e.g. a perp/spot tab whose underlying still has an
 * option chain), there's no option symbol to parse, so fall back to composing from
 * baseCurrency/quoteCurrency. This is the same normalization used for an option's
 * optional settlement suffix, keeping `BTC`, `BTC_USDT`, and `BTC_USDC` distinct.
 *
 * Returns null when no family can be resolved (caller should keep the current ticker).
 */
export function deriveOptionTickerFromInstrument(instrument: Instrument): string | null {
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) {
    return parseOptionSymbol(instrument)?.ticker ?? null
  }
  const base = instrument.baseCurrency?.toUpperCase()
  if (!base) return null
  const quote = instrument.quoteCurrency?.toUpperCase()
  return composeOptionTicker(base, quote)
}

/**
 * Build the args for `openInstrumentTab` to open a specific option contract as a chart
 * tab. `mode` is explicit at the call site: 'replace-current' for the debounced
 * single-click behavior, 'new-tab' for double-click and the row context menu.
 */
export function buildOpenOptionTabArgs(
  symbol: string,
  optionSymbolMap: Map<string, ParsedSymbol>,
  options: { mode: 'replace-current' | 'new-tab'; exchange: Exchange }
): OpenInstrumentTabParams {
  const parsed = resolveOptionSymbol(symbol, optionSymbolMap)
  return {
    symbol,
    type: 'options',
    exchange: toExchangeString(options.exchange),
    expiry: parsed?.expiry,
    expirationTsMs: parsed?.expirationTsMs,
    ...(options.mode === 'replace-current' && { replaceCurrentTab: true }),
  }
}

/**
 * Chain-cell text for an implied vol in percentage points (`"52.92"` → `"52.9%"`).
 * Deribit and Bybit send `0` when no vol reproduces the quote — typically a deep-ITM bid
 * at or below intrinsic, or no bid at all — so `0` means "undefined", not 0% vol, and
 * renders as `-` like a missing value.
 */
export function formatOptionIv(iv: string | undefined): string {
  const v = Number(iv)
  if (iv === undefined || !Number.isFinite(v) || v <= 0) return '-'
  return `${v.toFixed(1)}%`
}
